import { useState } from "react";
import api, { MAD, apiError } from "../lib/api";
import { useData } from "../hooks/useData";
import { useAuth } from "../context/AuthContext";
import { PageHeader, ExportButtons, StatCard, Loader, Empty } from "../components/ui-kit";
import { Modal, Field } from "../components/Modal";
import { exportExcel, exportPDF } from "../lib/exporters";
import { Snowflake, LogOut, Trash2, AlertTriangle, Plus } from "lucide-react";
import { toast } from "sonner";

const DESTINATIONS = [
  { v: "boutique_principale", l: "Boutique principale" },
  { v: "stand_foire", l: "Stand Foire" },
  { v: "b2b", l: "Distribution B2B" },
  { v: "glovo", l: "Glovo" },
  { v: "destruction", l: "Destruction" },
  { v: "autre", l: "Autre" },
];
const destLabel = (v) => DESTINATIONS.find((d) => d.v === v)?.l || v;

export default function Freezer() {
  const { isAdmin, can } = useAuth();
  const { data, loading, refetch } = useData("/freezer");
  const { data: products } = useData("/products");
  const [filter, setFilter] = useState("disponible");

  const list = data.filter((b) => (filter === "all" ? true : b.statut === filter));
  const dispo = data.filter((b) => b.statut === "disponible");
  const totalQte = dispo.reduce((a, b) => a + (b.quantite_restante || 0), 0);
  const totalVal = dispo.reduce((a, b) => a + (b.quantite_restante || 0) * (b.cout_unitaire || 0), 0);
  const epuises = data.filter((b) => b.statut === "epuise").length;

  const [open, setOpen] = useState(false);
  const [batch, setBatch] = useState(null);
  const [form, setForm] = useState(null);

  const openSortie = (b) => {
    setBatch(b);
    setForm({ quantite: "", destination: "boutique_principale", date: new Date().toISOString().slice(0, 10), heure: "", observation: "" });
    setOpen(true);
  };

  const saveSortie = async (e) => {
    e.preventDefault();
    const qte = parseFloat(form.quantite || 0);
    if (qte <= 0) return toast.error("Quantité invalide");
    if (qte > batch.quantite_restante) return toast.error(`Maximum disponible pour ce lot : ${batch.quantite_restante}`);
    try {
      await api.post("/freezer/sortie", {
        batch_id: batch.id, quantite: qte, destination: form.destination,
        date: form.date ? new Date(form.date).toISOString() : "", heure: form.heure, observation: form.observation,
      });
      toast.success("Sortie enregistrée — stock congélateur mis à jour"); setOpen(false); refetch();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Supprimer le lot ${b.numero_lot} du congélateur ?`)) return;
    await api.delete(`/freezer/${b.id}`); toast.success("Lot supprimé"); refetch();
  };

  // --- Entrée manuelle au congélateur ---
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(null);
  const openAdd = () => {
    setAddForm({ product_id: products[0]?.id || "", quantite: "", numero_lot: "", date_entree: new Date().toISOString().slice(0, 10), commentaire: "" });
    setAddOpen(true);
  };
  const saveAdd = async (e) => {
    e.preventDefault();
    const qte = parseFloat(addForm.quantite || 0);
    if (!addForm.product_id) return toast.error("Choisissez un produit");
    if (qte <= 0) return toast.error("Quantité invalide");
    try {
      await api.post("/freezer", {
        product_id: addForm.product_id, quantite: qte, numero_lot: addForm.numero_lot,
        date_entree: addForm.date_entree ? new Date(addForm.date_entree).toISOString() : "", commentaire: addForm.commentaire,
      });
      toast.success("Entrée congélateur enregistrée"); setAddOpen(false); refetch();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const fmt = (s) => (s ? new Date(s).toLocaleDateString("fr-FR") : "—");
  const cols = [
    { header: "Lot", value: (b) => b.numero_lot }, { header: "Produit", value: (b) => b.product_nom },
    { header: "Catégorie", value: (b) => b.categorie }, { header: "Date production", value: (b) => fmt(b.date_production) },
    { header: "Entrée congélateur", value: (b) => fmt(b.date_entree) }, { header: "Qté initiale", value: (b) => b.quantite_initiale },
    { header: "Qté restante", value: (b) => b.quantite_restante }, { header: "Statut", value: (b) => b.statut },
  ];

  return (
    <div>
      <PageHeader title="Stock Congélateur" subtitle="Produits finis congelés — saisie manuelle, traçabilité par numéro de lot.">
        {can("congelateur", "export") && <ExportButtons onExcel={() => exportExcel("stock_congelateur", cols, data)} onPDF={() => exportPDF("Stock Congélateur TIRAMESSI", cols, data)} />}
        {can("congelateur", "create") && <button className="tm-btn" onClick={openAdd} data-testid="add-freezer-btn"><Plus size={16} /> Ajouter au congélateur</button>}
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Produits congelés" value={totalQte} icon={Snowflake} accent="#4FC3F7" testid="freezer-nb" />
        <StatCard label="Valeur estimée" value={MAD(totalVal)} icon={Snowflake} accent="#D4AF37" testid="freezer-val" />
        <StatCard label="Lots disponibles" value={dispo.length} testid="freezer-dispo" />
        <StatCard label="Lots épuisés" value={epuises} testid="freezer-epuises" />
      </div>

      <div className="flex gap-2 mb-6" data-testid="freezer-filter">
        {[["disponible", "Disponibles"], ["epuise", "Épuisés"], ["all", "Tous"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className="px-3 py-1.5 rounded-md text-sm font-semibold transition-colors"
            style={filter === k ? { background: "#4FC3F7", color: "#000" } : { background: "var(--tm-surface-2)", color: "var(--tm-muted)" }}>{l}</button>
        ))}
      </div>

      {loading ? <Loader /> : list.length === 0 ? <Empty text="Aucun lot au congélateur. Cliquez sur « Ajouter au congélateur » pour saisir une entrée manuelle." /> : (
        <div className="space-y-3" data-testid="freezer-list">
          {list.map((b) => {
            const ancien = b.statut === "disponible" && b.date_entree && (Date.now() - new Date(b.date_entree)) > 21 * 864e5;
            return (
              <div key={b.id} className="tm-card p-5" data-testid={`freezer-batch-${b.id}`} style={{ opacity: b.statut === "epuise" ? 0.6 : 1 }}>
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-semibold text-lg flex items-center gap-2 flex-wrap">
                      {b.product_nom}
                      <span className="tm-badge" style={{ background: "rgba(212,175,55,.15)", color: "var(--tm-gold)" }}>{b.numero_lot}</span>
                      <span className="tm-badge" style={b.statut === "epuise" ? { background: "rgba(244,67,54,.15)", color: "#F44336" } : { background: "rgba(79,195,247,.15)", color: "#4FC3F7" }}>{b.statut === "epuise" ? "Épuisé / clôturé" : "Disponible"}</span>
                      {ancien && <span className="tm-badge" style={{ background: "rgba(255,152,0,.15)", color: "#FF9800" }}><AlertTriangle size={12} /> Lot ancien</span>}
                    </p>
                    <p className="text-xs text-[var(--tm-muted)]">{b.categorie} · Production {fmt(b.date_production)} · Entrée {fmt(b.date_entree)}</p>
                  </div>
                  <div className="flex gap-2">
                    {b.statut === "disponible" && (can("congelateur", "create") || can("congelateur", "validate")) && <button onClick={() => openSortie(b)} className="tm-btn-ghost tm-btn text-sm" data-testid={`freezer-sortie-${b.id}`}><LogOut size={15} /> Sortie</button>}
                    {can("congelateur", "delete") && <button onClick={() => remove(b)} className="text-[var(--tm-muted)] hover:text-[#F44336]"><Trash2 size={16} /></button>}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div className="tm-card p-2.5" style={{ background: "var(--tm-bg)" }}><p className="text-[10px] uppercase tracking-wider text-[var(--tm-muted)]">Qté initiale</p><p className="font-display text-lg tabular">{b.quantite_initiale}</p></div>
                  <div className="tm-card p-2.5" style={{ background: "var(--tm-bg)" }}><p className="text-[10px] uppercase tracking-wider text-[var(--tm-muted)]">Qté restante</p><p className="font-display text-lg tabular" style={{ color: "#4FC3F7" }}>{b.quantite_restante}</p></div>
                  <div className="tm-card p-2.5" style={{ background: "var(--tm-bg)" }}><p className="text-[10px] uppercase tracking-wider text-[var(--tm-muted)]">Valeur restante</p><p className="font-display text-lg tabular">{MAD((b.quantite_restante || 0) * (b.cout_unitaire || 0))}</p></div>
                </div>
                {(b.sorties || []).length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs uppercase tracking-wider text-[var(--tm-muted)] mb-1">Sorties</p>
                    {b.sorties.map((s, i) => (
                      <div key={i} className="flex justify-between text-sm py-1" style={{ borderBottom: "1px solid var(--tm-border)" }}>
                        <span>{s.quantite} → <span style={{ color: s.destination === "destruction" ? "#F44336" : "var(--tm-beige)" }}>{destLabel(s.destination)}</span> {s.observation && <span className="text-[var(--tm-muted)]">· {s.observation}</span>}</span>
                        <span className="text-[var(--tm-muted)] text-xs">{fmt(s.date)} {s.heure}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={`Sortie congélateur — ${batch?.numero_lot || ""}`} testid="freezer-sortie-modal">
        {form && batch && (
          <form onSubmit={saveSortie}>
            <p className="text-sm text-[var(--tm-muted)] mb-3">{batch.product_nom} · Disponible : <span style={{ color: "#4FC3F7" }}>{batch.quantite_restante}</span></p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Quantité sortie"><input type="number" step="0.01" min="0" max={batch.quantite_restante} className="tm-input" required value={form.quantite} onChange={(e) => setForm({ ...form, quantite: e.target.value })} data-testid="freezer-sortie-qty" /></Field>
              <Field label="Destination"><select className="tm-input" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="freezer-sortie-dest">{DESTINATIONS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}</select></Field>
              <Field label="Date"><input type="date" className="tm-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
              <Field label="Heure"><input type="time" className="tm-input" value={form.heure} onChange={(e) => setForm({ ...form, heure: e.target.value })} /></Field>
            </div>
            <Field label="Observation"><input className="tm-input" value={form.observation} onChange={(e) => setForm({ ...form, observation: e.target.value })} /></Field>
            <button type="submit" className="tm-btn w-full mt-1" data-testid="freezer-sortie-save">Valider la sortie</button>
          </form>
        )}
      </Modal>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Ajouter au Stock Congélateur" testid="freezer-add-modal">
        {addForm && (
          <form onSubmit={saveAdd}>
            <Field label="Produit">
              <select className="tm-input" value={addForm.product_id} onChange={(e) => setAddForm({ ...addForm, product_id: e.target.value })} data-testid="freezer-add-product">
                {products.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Quantité"><input type="number" step="0.01" min="0" className="tm-input" required value={addForm.quantite} onChange={(e) => setAddForm({ ...addForm, quantite: e.target.value })} data-testid="freezer-add-qty" /></Field>
              <Field label="Date d'entrée"><input type="date" className="tm-input" value={addForm.date_entree} onChange={(e) => setAddForm({ ...addForm, date_entree: e.target.value })} data-testid="freezer-add-date" /></Field>
            </div>
            <Field label="Numéro de lot (laisser vide pour génération auto)"><input className="tm-input" placeholder="Auto (CONG-...)" value={addForm.numero_lot} onChange={(e) => setAddForm({ ...addForm, numero_lot: e.target.value })} data-testid="freezer-add-lot" /></Field>
            <Field label="Commentaire"><input className="tm-input" value={addForm.commentaire} onChange={(e) => setAddForm({ ...addForm, commentaire: e.target.value })} data-testid="freezer-add-comment" /></Field>
            <button type="submit" className="tm-btn w-full mt-1" data-testid="freezer-add-save">Ajouter au congélateur</button>
          </form>
        )}
      </Modal>
    </div>
  );
}
